import BenchlingConfig from 'BenchlingConfig';
import {LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {DedicatedLookupColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  BulkLookupRequest,
  doLookupColumnQuery,
} from 'notebook/structured-tables/lookup-columns/LookupColumnsDAO';
import {parseIdFromLookupInputTypedValues} from 'notebook/structured-tables/util/TypedValueAdapters';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {CellError} from 'notebook/tables/excess/Util';
import {
  CustomResolutionQueryType,
  LookupColumnQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';

import {getBulkLookupResult} from './BatchedBulkLookupColumns';

export async function bulkResolveLookupColumnToServerResolutionResult(
  queries: Array<LookupColumnQuery>
): Promise<Array<ServerResolutionResult>> {
  if (!queries.every((query) => query.lookupConfigId === queries[0].lookupConfigId)) {
    throw Error('Expected all batched lookup queries to have the same lookupConfigID');
  }
  if (!queries.every((query) => query.fileId === queries[0].fileId)) {
    throw Error('Expected all batched lookup queries to come from the same document');
  }
  if (BenchlingConfig.ELN_ENABLE_BATCHED_BULK_LOOKUP_COLUMNS) {
    const bulkRequest: BulkLookupRequest = {
      lookupConfigId: queries[0].lookupConfigId,
      lookupInputIds: queries.map((q) => q.inputValueId),
      lookupSortType: queries[0].lookupSortType,
      fileId: queries[0].fileId,
    };
    const queryResult = await getBulkLookupResult(bulkRequest);
    return queryResult.serverResolutionResults;
  } else {
    const queryResult = await doLookupColumnQuery(
      queries[0].lookupConfigId,
      queries.map((q) => q.inputValueId),
      queries[0].lookupSortType,
      queries[0].fileId
    );
    return queryResult.serverResolutionResults;
  }
}

export function lookupColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: DedicatedLookupColumnDefinition,
  cellDependencies: [TypedValue | null],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn,
  _resolveTypedValueToType: unknown,
  fileId: string | null
): CustomEvalResult {
  const inputTypedValue = cellDependencies[0];
  if (!inputTypedValue) {
    return {
      val: {type: BaseTypedValueType.STRING, rawValue: ''},
      error: null,
      locked: true,
    };
  }

  const inputValueIds = parseIdFromLookupInputTypedValues(inputTypedValue);
  // For now we only allow one input value per row
  if (!inputValueIds.length) {
    return {
      val: {type: BaseTypedValueType.STRING, rawValue: ''},
      error: null,
      locked: true,
    };
  }
  if (inputValueIds.length > 1) {
    return {
      val: {type: BaseTypedValueType.STRING, rawValue: ''},
      error: new CellError('Only one input value is allowed per row'),
      locked: true,
    };
  }
  const inputValueId = inputValueIds[0];
  const containsFilterByOrigin = columnDefinition.lookupConfig.lookupSteps.some(
    (step) => step.type === LookupStepType.FILTER_BY_ORIGIN
  );
  const {resolvedValue, error} = resolveFromResolutionQuery(
    {type: 'STRING', rawValue: ''}, // lookup columns should never have relevant data stored in the typedValue
    {
      customResolutionQueryType: CustomResolutionQueryType.LOOKUP_COLUMN,
      inputValueId,
      lookupConfigId: columnDefinition.lookupConfig.id,
      type: ResolutionQueryType.CUSTOM,
      ...(columnDefinition.lookupSortType && {lookupSortType: columnDefinition.lookupSortType}),
      ...(containsFilterByOrigin && {fileId}),
    },
    makeTypedValueFromCustomResolvedValue
  );

  return {val: resolvedValue, error, locked: true};
}
